/**
 * The grab edge of a docked right-hand panel: hold it and drag toward the
 * board to widen the panel, double-click to snap back to the default, arrow
 * keys to nudge. One component for the three panels so they resize
 * identically (ui/panel-width.ts holds the arithmetic and the memory).
 *
 * Pointer capture rather than document listeners: the pointer may leave the
 * 9 px strip on the first frame of a fast drag, and capture keeps the moves
 * coming until the button is released wherever that happens.
 */
import { useRef, useState, type KeyboardEvent, type PointerEvent } from "react";

import { widthFromDrag, PANEL_KEY_STEP, type PanelWidthBounds } from "./panel-width.js";

export interface PanelResizeHandleProps {
  /** What is being resized — the accessible name ("Resize the comments panel"). */
  label: string;
  width: number;
  bounds: PanelWidthBounds;
  onResize(width: number): void;
  onCommit(): void;
  onReset(): void;
}

const DRAGGING_CLASS = "panel-resizing";

/** The `.kola-root` this handle lives in — the element app.css's drag rule is scoped to. */
function dragRootOf(element: HTMLElement): Element {
  return element.closest(".kola-root") ?? document.body;
}

export function PanelResizeHandle({ label, width, bounds, onResize, onCommit, onReset }: PanelResizeHandleProps) {
  const [dragging, setDragging] = useState(false);
  const start = useRef<{ x: number; width: number } | null>(null);

  function onPointerDown(event: PointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    start.current = { x: event.clientX, width };
    setDragging(true);
    dragRootOf(event.currentTarget).classList.add(DRAGGING_CLASS);
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>) {
    if (start.current === null) return;
    onResize(widthFromDrag(start.current.width, start.current.x, event.clientX, bounds));
  }

  function endDrag(event: PointerEvent<HTMLDivElement>) {
    if (start.current === null) return;
    start.current = null;
    setDragging(false);
    dragRootOf(event.currentTarget).classList.remove(DRAGGING_CLASS);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    onCommit();
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    // Left widens (the panel grows toward the board), right narrows.
    const delta = event.key === "ArrowLeft" ? PANEL_KEY_STEP : event.key === "ArrowRight" ? -PANEL_KEY_STEP : 0;
    if (delta === 0) return;
    event.preventDefault();
    onResize(width + delta);
    onCommit();
  }

  return (
    <div
      className={`panel-resize-handle${dragging ? " dragging" : ""}`}
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width}
      aria-valuemin={bounds.min}
      aria-valuemax={bounds.max}
      tabIndex={0}
      title="Drag to resize · double-click to reset"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={onReset}
      onKeyDown={onKeyDown}
    />
  );
}
